'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import { CartItem } from '@apps/shared/types/cart';
import { apiClient } from '@/lib/api-client';
import { useAuthenticatedApiClient } from '@/lib/authenticated-api-client';
import { useUser } from '@clerk/nextjs';
import { useToast } from '@/hooks/use-toast';

interface CartContextType {
  items: CartItem[];
  loading: boolean;
  addItem: (productId: string, qty: number) => Promise<void>;
  removeItem: (productId: string) => Promise<void>;
  updateQuantity: (productId: string, qty: number) => Promise<void>;
  clearCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'cart_items';

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, isLoaded } = useUser();
  const { toast } = useToast();
  const authenticatedApiClient = useAuthenticatedApiClient();

  // Load initial cart data
  useEffect(() => {
    const loadCart = async () => {
      setLoading(true);
      try {
        if (user) {
          // Get local cart before fetching server cart
          const localCart = localStorage.getItem(CART_STORAGE_KEY);
          const localItems = localCart ? JSON.parse(localCart) : [];

          // Fetch server cart with retry logic
          await loadServerCartWithRetry(localItems);
        } else {
          // Load cart from localStorage for guests
          const storedCart = localStorage.getItem(CART_STORAGE_KEY);
          if (storedCart) {
            setItems(JSON.parse(storedCart));
          }
        }
      } catch (error) {
        console.error('Error loading cart:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCart();
  }, [user]);

  const loadServerCartWithRetry = async (localItems: CartItem[], attempt = 1) => {
    const maxAttempts = 5;
    const delays = [500, 1000, 2000, 4000, 8000]; // Exponential backoff in ms

    try {
      const { data } = await authenticatedApiClient.get('/cart');

      // If we have local items and just logged in, merge carts
      if (localItems.length > 0) {
        toast({
          title: 'Syncing your cart...',
          description: "We're adding your saved items to your account.",
        });
        await mergeCarts(localItems, data.items);
        toast({
          title: 'Cart synced!',
          description: 'Your items have been saved to your account.',
        });
        // Clear local storage after successful merge
        localStorage.removeItem(CART_STORAGE_KEY);
      } else {
        setItems(data.items);
      }
    } catch (error: any) {
      // Check if it's a 412 error (user account initializing)
      if (error?.response?.status === 412 && attempt < maxAttempts) {
        console.log(`User account initializing, retrying cart load in ${delays[attempt - 1]}ms (attempt ${attempt}/${maxAttempts})`);
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, delays[attempt - 1]));
        
        // Retry
        return loadServerCartWithRetry(localItems, attempt + 1);
      } else if (error?.response?.status === 412 && attempt >= maxAttempts) {
        // Final failure after all retries - fall back to local cart
        console.warn('Failed to load server cart after retries, using local cart');
        if (localItems.length > 0) {
          setItems(localItems);
        }
      } else {
        // Other errors, don't retry
        throw error;
      }
    }
  };

  // Sync cart to localStorage for guests
  useEffect(() => {
    if (!user) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    }
  }, [items, user]);

  const addItem = async (productId: string, qty: number) => {
    setLoading(true);
    try {
      console.log('Add to cart - user:', user, 'isLoaded:', isLoaded, 'user type:', typeof user);
      console.log('User properties:', user ? Object.keys(user) : 'no user');
      
      if (user) {
        // Add to server cart for authenticated users with retry logic
        console.log('Adding to server cart for authenticated user');
        await addItemWithRetry(productId, qty);
      } else {
        // Add to local cart for guests
        console.log('Adding to local cart for guest user');
        const response = await apiClient.get(`/products/${productId}`);
        const product = response.data;

        const existingItem = items.find(item => item.productId === productId);
        if (existingItem) {
          setItems(
              items.map(item =>
                  item.productId === productId ? { ...item, qty: qty } : item,
              ),
          );
        } else {
          const newItem: CartItem = {
            productId: product._id,
            slug: product.slug,
            name: product.name,
            image: product.images[0],
            price: product.price,
            countInStock: product.countInStock,
            category: product.category,
            subCategory: product.subCategory,
            qty,
          };
          setItems([...items, newItem]);
        }
      }
    } catch (error) {
      toast({
        title: 'Error adding item',
        description: 'There was a problem adding your item to the cart.',
        variant: 'destructive',
      });
      console.error('Error adding item to cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addItemWithRetry = async (productId: string, qty: number, attempt = 1) => {
    const maxAttempts = 5;
    const delays = [500, 1000, 2000, 4000, 8000]; // Exponential backoff in ms

    try {
      const { data } = await authenticatedApiClient.post('/cart/items', {
        productId,
        qty,
      });
      setItems(data.items);
    } catch (error: any) {
      // Check if it's a 412 error (user account initializing)
      if (error?.response?.status === 412 && attempt < maxAttempts) {
        console.log(`User account initializing, retrying in ${delays[attempt - 1]}ms (attempt ${attempt}/${maxAttempts})`);
        
        // Show toast for first attempt
        if (attempt === 1) {
          toast({
            title: 'Setting up your account...',
            description: 'Please wait while we prepare your account.',
          });
        }

        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, delays[attempt - 1]));
        
        // Retry
        return addItemWithRetry(productId, qty, attempt + 1);
      } else if (error?.response?.status === 412 && attempt >= maxAttempts) {
        // Final failure after all retries
        toast({
          title: 'Account setup failed',
          description: 'Unable to set up your account. Please try again later. Support has been notified.',
          variant: 'destructive',
        });
        throw error;
      } else {
        // Other errors, don't retry
        throw error;
      }
    }
  };

  const removeItem = async (productId: string) => {
    setLoading(true);
    try {
      if (user) {
        const { data } = await authenticatedApiClient.delete(`/cart/items/${productId}`);
        setItems(data.items);
      } else {
        setItems(items.filter(item => item.productId !== productId));
      }
      toast({
        title: 'Item removed',
        description: 'The item has been removed from your cart.',
      });
    } catch (error) {
      toast({
        title: 'Error removing item',
        description: 'There was a problem removing the item from your cart.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId: string, qty: number) => {
    setLoading(true);
    try {
      if (user) {
        const { data } = await authenticatedApiClient.put(`/cart/items/${productId}`, {
          qty,
        });
        setItems(data.items);
      } else {
        setItems(
            items.map(item =>
                item.productId === productId ? { ...item, qty } : item,
            ),
        );
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      if (user) {
        await authenticatedApiClient.delete('/cart');
      }
      setItems([]);
      toast({
        title: 'Cart cleared',
        description: 'All items have been removed from your cart.',
      });
    } catch (error) {
      toast({
        title: 'Error clearing cart',
        description: 'There was a problem clearing your cart.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const mergeCarts = async (
      localItems: CartItem[],
      serverItems: CartItem[],
  ) => {
    // Create a map of server items for quick lookup
    const serverItemsMap = new Map(
        serverItems.map(item => [item.productId, item]),
    );

    // Merge local items with server items
    for (const localItem of localItems) {
      const serverItem = serverItemsMap.get(localItem.productId);
      if (serverItem) {
        // If item exists in both carts, take the higher quantity
        await updateQuantity(
            localItem.productId,
            Math.max(localItem.qty, serverItem.qty),
        );
      } else {
        // If item only exists locally, add it to server cart
        await addItem(localItem.productId, localItem.qty);
      }
    }
  };

  return (
      <CartContext.Provider
          value={{
            items,
            loading,
            addItem,
            removeItem,
            updateQuantity,
            clearCart,
          }}
      >
        {children}
      </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};